import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  LanguageDropDownData,
  LOVListWithImageValue,
} from "../api/services/pageContent/models";
import {
  commonSiteCoreAccessDeniedLabelKeys,
  commonSiteCoreARIALabelKeys,
  commonSiteCoreKeys,
} from "../helpers/constants/siteCoreKeys";
import { replaceHashWithValue } from "../helpers/utils";
import { navigationSiteCoreContentSelector } from "../redux/slices/appState/appStateSelector";
import { KeyValueMap } from "../redux/slices/appState/models";

// To fetch data of the container like main header,footer & sidebar
export const useNavigationSiteCore = () => {
  const { Components: pageContent } = useSelector(
    navigationSiteCoreContentSelector,
  );
  /*
   * To fetch the language dropdown data
   * Example - fetchDropdownValues(SITE_CORE.clientSearchFormKey,"ClientManagement.SearchClient.FilterByList",);
   */
  const fetchLanguageDropdownValues = (
    optionByKey?: boolean,
  ): LanguageDropDownData[] => {
    if (pageContent?.[commonSiteCoreKeys.commonHeaderKey]?.LOVListWithImage) {
      const values =
        pageContent?.[
          commonSiteCoreKeys.commonHeaderKey
        ]?.LOVListWithImage.find(
          (el: { Key: string; Values: LOVListWithImageValue[] }) =>
            el.Key === commonSiteCoreKeys.headerLanguageKey,
        )?.Values || [];
      return values.map((data) => ({
        value: optionByKey ? data.Key : data.Title,
        label: data.Value,
        imagePath: data.ImagePath,
      }));
    }
    return [];
  };

  /*
   * To fetch the language value by key
   * Example - fetchLanguageValueByKey('en-US');
   */
  const fetchLanguageValueByKey = (key = ""): string => {
    const values = fetchLanguageDropdownValues();
    const value = values.find(
      (item) => item.value.toLowerCase() === key.toLowerCase(),
    );
    return value ? value.label : "";
  };

  // Function to fetch value by key with optimization using Map and useMemo
  const fetchValueByKeyOfLanguageDropdown = (
    parentKey: string,
    key: string,
  ): string => {
    if (pageContent?.[commonSiteCoreKeys.commonHeaderKey]?.LOVListWithImage) {
      const lovList =
        pageContent[commonSiteCoreKeys.commonHeaderKey].LOVListWithImage;

      const lovListMap = new Map<string, Map<string, string>>(
        lovList.map((el: { Key: string; Values: LOVListWithImageValue[] }) => [
          el.Key,
          new Map(el.Values.map((val) => [val.Key, val.Value])),
        ]),
      );

      // Access the value using the parentKey
      const parentValuesMap = lovListMap.get(parentKey);
      if (parentValuesMap) {
        return parentValuesMap.get(key) ?? "";
      }
    }
    return "";
  };

  /*
   * To fetch header label
   * Example - fetchCommonLabel("Common.EnterPlaceholderText")
   */
  const fetchHeaderLabel = (key: string): string => {
    return (
      pageContent?.[commonSiteCoreKeys.commonHeaderKey]?.Labels?.find(
        (el: { Key: string; Phrase: string }) => el.Key === key,
      )?.Phrase || ""
    );
  };

  const fetchComponentTitle = (key: string): string => {
    return pageContent?.[key]?.Title || "";
  };

  const fetchLabel = (parentKey: string, key: string): string => {
    if (pageContent?.[parentKey]?.Labels) {
      return (
        pageContent[parentKey].Labels.find(
          (el: { Key: string; Phrase: string }) => el.Key === key,
        )?.Phrase || ""
      );
    }
    return "";
  };

  /*  to fetch the mediaList image from the given parentKey and mediaKey
  fetchMediaListImage(
       clientSearchSiteCoreKeys.result,
       clientSearchSiteCoreKeys.noHierarchyImage,
       clientSearchSiteCoreKeys.smallImage,
  );
  */

  const fetchMediaListImage = (
    parentKey: string,
    mediaKey: string,
    imageType: string,
  ) => {
    // Check if the MediaList exists in the specified parentKey
    if (pageContent?.[parentKey]?.MediaList) {
      // Find the media object from the MediaList by matching the mediaKey
      const mediaItem = pageContent[parentKey].MediaList.find(
        (el: { Key: string }) => el.Key === mediaKey,
      );

      // If the media item is found and the imageType exists (like SmallImage, MediumImage, etc.)
      if (mediaItem && mediaItem[imageType]) {
        return mediaItem[imageType]; // Return the respective image object (e.g., SmallImage, MediumImage)
      }
    }

    // Return null or empty if the conditions are not met
    return null;
  };

  /*
   * To fetch common ARIA label
   * Example - fetchCommonARIALabel("enterPlaceholderTextKey")
   */
  const fetchCommonARIALabel = (
    key: keyof typeof commonSiteCoreARIALabelKeys,
  ): string => {
    return (
      pageContent?.[commonSiteCoreARIALabelKeys.ariaLabelDetails]?.Labels?.find(
        (el: { Key: string; Phrase: string }) =>
          el.Key === commonSiteCoreARIALabelKeys[key],
      )?.Phrase || ""
    );
  };

  const commonARIALabel: Record<
    keyof typeof commonSiteCoreARIALabelKeys,
    string
  > = useMemo(() => {
    const result: Record<string, string> = {};
    (
      Object.keys(commonSiteCoreARIALabelKeys) as Array<
        keyof typeof commonSiteCoreARIALabelKeys
      >
    ).forEach((key) => {
      result[key] = fetchCommonARIALabel(key);
    });
    return result as Record<keyof typeof commonSiteCoreARIALabelKeys, string>;
  }, [pageContent]);

  /*
   * fetchAccessDeniedLabels for fetching access denied-related labels
   */
  const fetchAccessDeniedLabels = (
    key: string,
    replacement?: KeyValueMap,
  ): string => {
    const message =
      pageContent?.[
        commonSiteCoreAccessDeniedLabelKeys.accessDeniedParentKey
      ]?.Labels?.find((el: { Key: string; Phrase: string }) => el.Key === key)
        ?.Phrase ?? "";

    if (replacement) {
      return replaceHashWithValue(message, replacement);
    }

    return message;
  };

  /*
   * Created fetchAccessDeniedLabels which will return access denied-related labels across the application
   * Example - fetchAccessDeniedLabels("AccessDenied.Title")
   */
  const commonAccessDeniedDetails = useMemo(
    () => ({
      accessDeniedParentKey: fetchComponentTitle(
        commonSiteCoreAccessDeniedLabelKeys.accessDeniedParentKey,
      ),
      accessDeniedTitle: fetchAccessDeniedLabels(
        commonSiteCoreAccessDeniedLabelKeys.accessDeniedTitle,
      ),
      accessDeniedDescription: fetchAccessDeniedLabels(
        commonSiteCoreAccessDeniedLabelKeys.accessDeniedDescription,
      ),
      backToDashboardButton: fetchAccessDeniedLabels(
        commonSiteCoreAccessDeniedLabelKeys.backToDashboardButton,
      ),
    }),
    [pageContent],
  );

  return {
    fetchLanguageDropdownValues,
    fetchLanguageValueByKey,
    fetchHeaderLabel,
    fetchValueByKeyOfLanguageDropdown,
    fetchComponentTitle,
    fetchLabel,
    fetchMediaListImage,
    fetchCommonARIALabel,
    commonARIALabel,
    commonAccessDeniedDetails,
  };
};
